import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ChevronsLeft, LogOut, Menu as MenuIcon, Search, X } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/cn'
import { useAuth } from '@/app/providers/AuthProvider'
import { useTenant } from '@/app/providers/TenantProvider'
import { visibleNavigation } from '@/app/config/navigation'
import { Avatar } from '@/components/ui/Avatar'
import { Button, IconButton } from '@/components/ui/Button'
import { Menu, MenuItem, MenuLabel, MenuSeparator, TooltipProvider } from '@/components/ui/Menu'
import { CommandPalette } from '@/components/shared/CommandPalette'
import { TenantSwitcher } from '@/components/shared/TenantSwitcher'

const SIDEBAR_KEY = 'tf.sidebar-collapsed'

export function AppShell() {
  const location = useLocation()
  const { user, signOut, role } = useAuth()
  const { branding, permissions, isLoading } = useTenant()
  const queryClient = useQueryClient()

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1'
    } catch {
      return false
    }
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0')
    } catch {
      /* preference will not persist */
    }
  }, [collapsed])

  // Close the mobile drawer on navigation, or it covers the page you just opened.
  useEffect(() => setMobileOpen(false), [location.pathname])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const groups = visibleNavigation(permissions)

  async function handleSignOut() {
    await signOut()
    queryClient.clear()
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-canvas">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:shadow-md"
        >
          Skip to content
        </a>

        {/* ---- sidebar ---- */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border bg-surface transition-[width,transform]',
            collapsed ? 'w-16' : 'w-60',
            mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          )}
          aria-label="Main navigation"
        >
          <div className={cn('flex h-14 items-center gap-2 border-b border-border px-3', collapsed && 'justify-center px-0')}>
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="size-7 rounded object-contain" />
            ) : (
              <div className="flex size-7 shrink-0 items-center justify-center rounded bg-accent text-xs font-semibold text-accent-fg">
                {(branding.displayName || 'TF').slice(0, 2).toUpperCase()}
              </div>
            )}
            {!collapsed ? (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-fg">
                  {isLoading ? ' ' : branding.displayName}
                </p>
                <p className="truncate text-2xs text-fg-subtle">Inspection admin</p>
              </div>
            ) : null}
            <IconButton
              label="Close navigation"
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileOpen(false)}
            >
              <X className="size-4" aria-hidden />
            </IconButton>
          </div>

          <nav className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3">
            {groups.map((group) => (
              <div key={group.label ?? 'root'} className="mb-4">
                {group.label && !collapsed ? (
                  <p className="px-2 pb-1 text-2xs font-medium uppercase tracking-wide text-fg-subtle">
                    {group.label}
                  </p>
                ) : null}
                <ul className="space-y-0.5">
                  {group.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={!item.matchPrefix}
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                            collapsed && 'justify-center px-0',
                            isActive
                              ? 'bg-accent-subtle text-accent'
                              : 'text-fg-muted hover:bg-surface-sunken hover:text-fg',
                          )
                        }
                      >
                        <item.icon className="size-4 shrink-0" aria-hidden />
                        {!collapsed ? <span className="truncate">{item.label}</span> : null}
                        {collapsed ? <span className="sr-only">{item.label}</span> : null}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={() => setCollapsed((value) => !value)}
              className={cn(
                'hidden w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-fg-subtle transition-colors hover:bg-surface-sunken hover:text-fg lg:flex',
                collapsed && 'justify-center px-0',
              )}
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              <ChevronsLeft className={cn('size-4 transition-transform', collapsed && 'rotate-180')} aria-hidden />
              {!collapsed ? 'Collapse' : null}
            </button>
          </div>
        </aside>

        {mobileOpen ? (
          <div
            className="fixed inset-0 z-30 bg-fg/30 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
        ) : null}

        {/* ---- main ---- */}
        <div className={cn('transition-[padding]', collapsed ? 'lg:pl-16' : 'lg:pl-60')}>
          <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-surface/95 px-4 backdrop-blur">
            <IconButton
              label="Open navigation"
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon className="size-4" aria-hidden />
            </IconButton>

            <Button
              variant="secondary"
              size="sm"
              className="w-48 justify-start text-fg-subtle"
              onClick={() => setPaletteOpen(true)}
              leadingIcon={<Search className="size-3.5" aria-hidden />}
            >
              <span className="flex-1 text-left">Search</span>
              <kbd className="rounded border border-border bg-surface-sunken px-1 font-mono text-2xs">⌘K</kbd>
            </Button>

            <div className="ml-auto flex items-center gap-2">
              <TenantSwitcher />
              <Menu
                trigger={
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-surface-sunken"
                    aria-label="Account menu"
                  >
                    <Avatar name={user?.name} size="md" />
                    <span className="hidden text-left sm:block">
                      <span className="block max-w-[10rem] truncate text-xs font-medium text-fg">
                        {user?.name}
                      </span>
                      <span className="block text-2xs capitalize text-fg-subtle">{role}</span>
                    </span>
                  </button>
                }
              >
                <MenuLabel>{user?.phone}</MenuLabel>
                <MenuSeparator />
                <MenuItem icon={<LogOut className="size-3.5" aria-hidden />} onSelect={() => void handleSignOut()}>
                  Sign out
                </MenuItem>
              </Menu>
            </div>
          </header>

          <main id="main" className="mx-auto max-w-[1600px] px-4 py-5 lg:px-6">
            <Outlet />
          </main>

          {branding.poweredBy ? (
            <footer className="px-6 pb-6 text-2xs text-fg-subtle">{branding.poweredBy}</footer>
          ) : null}
        </div>

        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      </div>
    </TooltipProvider>
  )
}
